export const DEFAULT_GUEST_GROUPS = [
  { id: "adult", abbreviation: "A", translationKey: "adults", fallbackName: "Adults" },
  { id: "kid", abbreviation: "K", translationKey: "kids", fallbackName: "Kids" },
  { id: "senior", abbreviation: "S", translationKey: "seniors", fallbackName: "Seniors" },
  { id: "other", abbreviation: "O", translationKey: "other", fallbackName: "Other" },
];

export const DEFAULT_GUEST_GROUP_IDS = DEFAULT_GUEST_GROUPS.map((group) => group.id);

export const isDefaultGuestGroup = (id) => DEFAULT_GUEST_GROUP_IDS.includes(id);

export const normalizeAbbreviation = (text) =>
  String(text || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);

export const suggestAbbreviation = (name, taken = []) => {
  const letters = normalizeAbbreviation(name);
  const used = new Set(taken.map(normalizeAbbreviation));
  for (let length = 1; length <= Math.min(3, letters.length); length += 1) {
    const candidate = letters.slice(0, length);
    if (!used.has(candidate)) return candidate;
  }
  return letters.slice(0, 1);
};

export const storeGuestGroups = (store) => {
  const saved = Array.isArray(store?.ageGroups) ? store.ageGroups.filter((group) => group?.id) : [];
  const savedById = new Map(saved.map((group) => [group.id, group]));
  const defaults = DEFAULT_GUEST_GROUPS.map((group) => {
    const override = savedById.get(group.id) || {};
    return {
      ...group,
      name: String(override.name || "").trim(),
      abbreviation: normalizeAbbreviation(override.abbreviation) || group.abbreviation,
      isDefault: true,
    };
  });
  const added = saved
    .filter((group) => !isDefaultGuestGroup(group.id))
    .map((group) => ({
      id: group.id,
      name: String(group.name || "").trim(),
      abbreviation: normalizeAbbreviation(group.abbreviation) || suggestAbbreviation(group.name),
      isDefault: false,
    }));
  return [...defaults, ...added];
};

export const guestGroupName = (group, translation = {}) =>
  group?.name || (group?.translationKey && translation[group.translationKey]) || group?.fallbackName || group?.id || "";

export const guestGroupLabel = (group, translation = {}) =>
  `${guestGroupName(group, translation)} (${group?.abbreviation || ""})`;

const groupCounts = (guests) => Object.entries(guests && typeof guests === "object" ? guests : {})
  .filter(([key]) => key !== "total");

export const guestTotal = (guests) =>
  groupCounts(guests).reduce((sum, [, value]) => sum + (Number(value) > 0 ? Number(value) : 0), 0);

export const guestSummary = (guests, groups = storeGuestGroups(null)) => {
  const counts = Object.fromEntries(groupCounts(guests));
  const known = new Set(groups.map((group) => group.id));
  const parts = groups
    .filter((group) => Number(counts[group.id]) > 0)
    .map((group) => `${group.abbreviation}${Number(counts[group.id])}`);
  Object.entries(counts).forEach(([id, value]) => {
    if (!known.has(id) && Number(value) > 0) parts.push(`${normalizeAbbreviation(id).slice(0, 1) || "?"}${Number(value)}`);
  });
  return parts.join(" ");
};

export const toSavedGuestGroups = (groups) =>
  groups.map((group) => ({
    id: group.id,
    name: String(group.name || "").trim(),
    abbreviation: normalizeAbbreviation(group.abbreviation),
  }));
